/**
 * Universal destructive-action confirmation modal.
 *
 * Built on Radix Dialog (via @/components/ui/dialog) so it inherits
 * focus-trap, initial-focus, return-focus, Esc-to-close, and proper
 * role="dialog"/aria-modal/aria-labelledby semantics for free.
 *
 * The previous bespoke version had none of those, AND used dynamic
 * Tailwind class names (`bg-${variant === 'danger' ? 'red' : ...}-100`)
 * which Tailwind cannot generate at build time — so the icon
 * background never rendered, leaving the danger/warning/info state
 * conveyed only by colour. The static map below fixes that.
 */

import React from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogBody,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  variant?: 'danger' | 'warning' | 'info';
}

// Static palette per variant. Hex literals because Tailwind utility
// classes are statically extracted — the previous template literal
// `bg-${variant}-100` produced strings the JIT never saw and silently
// rendered nothing. These values mirror Tailwind's red-100 / yellow-100
// / blue-100 + 700 text shades (700 is AA on the 100 fill).
const VARIANT_PALETTE: Record<
  NonNullable<ConfirmationModalProps['variant']>,
  { iconBg: string; iconFg: string; buttonVariant: 'destructive' | 'warning' | 'primary' }
> = {
  danger: {
    iconBg: '#fee2e2', // red-100
    iconFg: '#b91c1c', // red-700
    buttonVariant: 'destructive',
  },
  warning: {
    iconBg: '#fef3c7', // amber-100
    iconFg: '#b45309', // amber-700 — passes AA on amber-100
    buttonVariant: 'warning',
  },
  info: {
    iconBg: '#dbeafe', // blue-100
    iconFg: '#1d4ed8', // blue-700
    buttonVariant: 'primary',
  },
};

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  variant = 'warning',
}) => {
  const palette = VARIANT_PALETTE[variant];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isLoading) onClose();
      }}
    >
      <DialogContent
        // Block close while the destructive op is in flight so an
        // accidental Esc/click-outside doesn't leave the caller in an
        // ambiguous "did it cancel? did the mutation finish?" state.
        onInteractOutside={(e) => {
          if (isLoading) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isLoading) e.preventDefault();
        }}
      >
        <DialogHeader className="items-center text-center">
          <div
            aria-hidden
            className="flex items-center justify-center w-14 h-14 rounded-full mb-2"
            style={{ backgroundColor: palette.iconBg }}
          >
            <AlertTriangle
              className="w-7 h-7"
              style={{ color: palette.iconFg }}
            />
          </div>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>

        <DialogBody className="sr-only">
          {/* Spacer body so DialogFooter has spacing even when message
              fits in the description above. Hidden from SR to avoid
              announcing an empty region. */}
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="sm:flex-1"
          >
            {cancelText}
          </Button>
          <Button
            variant={palette.buttonVariant}
            onClick={onConfirm}
            disabled={isLoading}
            className="sm:flex-1"
            // Initial keyboard focus — Radix focuses the first
            // focusable child. Cancel comes first so destructive
            // confirms aren't fired by an over-eager Enter.
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </>
            ) : (
              confirmText
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
